// Pure geometry helpers for "table + chairs" diagrams. Used both by the
// AdvancedTablesPage editor (Konva canvas) and the read-only PlanView (SVG)
// in the Reservations workspace, so the two views always agree on what a
// table-with-chairs looks like.

export const CHAIR_THICK = 7; // chair depth, perpendicular to the table edge
export const CHAIR_GAP = 3; // gap between a chair and the table edge

// Spread `seats` chairs across the four sides as evenly as possible; top and
// bottom (the "long" edges, visually) take any leftovers first.
function chairsPerSide(seats: number) {
  const n = Math.max(0, Math.min(seats, 24));
  const base = Math.floor(n / 4);
  const sides = { top: base, bottom: base, right: base, left: base };
  const order: (keyof typeof sides)[] = ['top', 'bottom', 'right', 'left'];
  for (let i = 0; i < n % 4; i++) sides[order[i]] += 1;
  return sides;
}

const chairLen = (count: number, sideLen: number) =>
  count > 0 ? Math.max(7, Math.min(16, (sideLen - 6) / count - 3)) : 0;

export type ChairRect = {
  key: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

export type ChairCircleSlot = ChairRect & {
  offsetX: number;
  offsetY: number;
  rotation: number;
};

export function rectChairs(w: number, h: number, seats: number): ChairRect[] {
  const s = chairsPerSide(seats);
  const out: ChairRect[] = [];
  const lt = chairLen(s.top, w);
  for (let i = 0; i < s.top; i++)
    out.push({
      key: `t${i}`,
      x: (w * (i + 0.5)) / s.top - lt / 2,
      y: -(CHAIR_THICK + CHAIR_GAP),
      width: lt,
      height: CHAIR_THICK,
    });
  const lb = chairLen(s.bottom, w);
  for (let i = 0; i < s.bottom; i++)
    out.push({
      key: `b${i}`,
      x: (w * (i + 0.5)) / s.bottom - lb / 2,
      y: h + CHAIR_GAP,
      width: lb,
      height: CHAIR_THICK,
    });
  const lr = chairLen(s.right, h);
  for (let i = 0; i < s.right; i++)
    out.push({
      key: `r${i}`,
      x: w + CHAIR_GAP,
      y: (h * (i + 0.5)) / s.right - lr / 2,
      width: CHAIR_THICK,
      height: lr,
    });
  const ll = chairLen(s.left, h);
  for (let i = 0; i < s.left; i++)
    out.push({
      key: `l${i}`,
      x: -(CHAIR_THICK + CHAIR_GAP),
      y: (h * (i + 0.5)) / s.left - ll / 2,
      width: CHAIR_THICK,
      height: ll,
    });
  return out;
}

export function circleChairs(w: number, h: number, seats: number): ChairCircleSlot[] {
  const n = Math.max(0, Math.min(seats, 24));
  if (n === 0) return [];
  const cx = w / 2;
  const cy = h / 2;
  const R = Math.min(w, h) / 2;
  const dist = R + CHAIR_GAP + CHAIR_THICK / 2;
  const len = Math.max(7, Math.min(16, (2 * Math.PI * R) / n - 4));
  return Array.from({ length: n }, (_, i) => {
    const ang = (i / n) * Math.PI * 2 - Math.PI / 2;
    return {
      key: `c${i}`,
      x: cx + Math.cos(ang) * dist,
      y: cy + Math.sin(ang) * dist,
      width: len,
      height: CHAIR_THICK,
      offsetX: len / 2,
      offsetY: CHAIR_THICK / 2,
      rotation: (ang * 180) / Math.PI + 90,
    };
  });
}
